/**
 * Nearby test data — DEV ONLY.
 *
 * Synthetic profile connections for previewing the Nearby grouping (the
 * headerless near band, far-city headers ordered by distance, and the country
 * catch-alls) without doing real exchanges. Held in memory and merged into the
 * useNearbyGroups input behind a dev toggle — NO DB writes, so it flips on/off
 * instantly and never touches tester data. Gated to non-production builds at
 * the call site (REACT_APP_BRANCH !== 'main').
 *
 * Cities are clusters across a few metros. Set YOUR OWN Hometown to one of them
 * (e.g. New York, Los Angeles, San Francisco) to watch that cluster fall into
 * the headerless "near you" band while the rest become far-city sections.
 */

// Build a connection-shaped object that passes useNearbyGroups' profile-peer
// filter (`!isManualContact && anonData.senderProfileUserId`) and carries a
// shared hometown string (`currentCity`) for the grouping geocode. A shared
// hometown must carry its consent flag (isHometownShared gates the fallback),
// so records with a city get `sharedFields: ['currentCity']` — same shape as
// a real shared record.
const mk = (id, name, city, metDate) => ({
  chatRoomId: `__nearby_test_${id}`,
  isManualContact: false,
  displayName: null,
  latestMessage: null,
  notes: { text: '' },
  connectionMetadata: { meetingDate: `${metDate}T12:00:00Z` },
  anonData: {
    name,
    senderProfileUserId: `__test_pid_${id}`,
    currentCity: city,
    photoUrl: null,
    sharedFields: city ? ['currentCity'] : [],
  },
});

export const NEARBY_TEST_DATA = {
  // Small set — enough to show a far-city header + the country catch-alls.
  // Set your Hometown to New York to see Ada/Alan fall into the near band.
  light: [
    mk('l_ny1', 'Ada Lovelace', 'New York, NY', '2026-06-17'),
    mk('l_ny2', 'Alan Turing', 'New York, NY', '2026-06-15'),
    mk('l_chi', 'Studs Terkel', 'Chicago, IL', '2026-06-10'),
    mk('l_lon', 'Mary Shelley', 'London', '2026-05-30'),
  ],
  // Full set — exercises every branch: near band (whichever metro is yours),
  // multiple far-city headers ordered by distance, "Elsewhere in <country>",
  // and "International". Includes a couple of suburbs to show the near band
  // capturing the metro by distance (Pasadena↔LA, Oakland↔SF, Brooklyn↔NYC).
  full: [
    mk('f_ny1', 'Ada Lovelace', 'New York, NY', '2026-06-18'),
    mk('f_ny2', 'Alan Turing', 'New York, NY', '2026-06-16'),
    mk('f_ny3', 'Dorothy Parker', 'Brooklyn, New York', '2026-06-12'),
    mk('f_la1', 'Joan Didion', 'Los Angeles, CA', '2026-06-17'),
    mk('f_la2', 'Ray Bradbury', 'Los Angeles, CA', '2026-06-13'),
    mk('f_la3', 'Octavia Butler', 'Pasadena, CA', '2026-06-11'), // suburb — still merges
    mk('f_sf1', 'Ansel Adams', 'San Francisco, CA', '2026-06-14'),
    mk('f_sf2', 'Dashiell Hammett', 'San Francisco, CA', '2026-06-10'),
    mk('f_sf3', 'Maya Angelou', 'Oakland, CA', '2026-06-09'), // suburb — still merges
    mk('f_chi', 'Studs Terkel', 'Chicago, IL', '2026-06-08'),
    mk('f_mia', 'Gloria Estefan', 'Miami, FL', '2026-06-05'),
    mk('f_lon', 'Mary Shelley', 'London', '2026-05-30'),
    mk('f_par', 'Simone de Beauvoir', 'Paris', '2026-05-28'),
    mk('f_tok', 'Hayao Miyazaki', 'Tokyo', '2026-05-20'),
  ],
  // Live set — these resolve via NEARBY_TEST_LIVE_RECORDS (coordinates), NOT a
  // hometown (currentCity is null). Demonstrates the live path + the
  // reverse-geocode fix: LA coords → "Los Angeles" (not Laredo), Las Vegas →
  // "Las Vegas, US" (not Lviv, Ukraine). Shown regardless of your sharing state.
  live: [
    mk('live_sf1', 'Janis Joplin', null, '2026-06-18'),
    mk('live_sf2', 'Carlos Santana', null, '2026-06-16'),
    mk('live_la1', 'Kendrick Lamar', null, '2026-06-15'),
    mk('live_la2', 'Snoop Dogg', null, '2026-06-13'),
    mk('live_lv1', 'Elvis Presley', null, '2026-06-11'),
  ],
};

// Synthetic live-location rows for the `live` set, keyed by chatRoomId. `city`
// is the ABBREVIATION a real row stores (the thing that forward-geocodes wrong),
// so this exercises the reverse-geocode-from-coords fix.
export const NEARBY_TEST_LIVE_RECORDS = {
  __nearby_test_live_sf1: {
    lat: 37.7599,
    lng: -122.4148,
    city: 'SF',
    neighborhood: 'Mission District',
  },
  __nearby_test_live_sf2: { lat: 37.7616, lng: -122.435, city: 'SF', neighborhood: 'The Castro' },
  __nearby_test_live_la1: { lat: 34.0407, lng: -118.2468, city: 'LA', neighborhood: 'Downtown' },
  __nearby_test_live_la2: { lat: 34.0211, lng: -118.3965, city: 'LA', neighborhood: 'Culver City' },
  __nearby_test_live_lv1: { lat: 36.1147, lng: -115.1728, city: 'LV', neighborhood: 'The Strip' },
};
